import type { ColumnDef } from '@tanstack/react-table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import type { UserMgmtResponseDTO } from '@/api/generated/model';
import { LoginProviderCell } from '@/lib/loginProviderIcon';
import { userTypeLabel } from '@/lib/enums';
import { userStatusDisplay } from './types';

function initials(name: string | undefined): string {
  if (!name) return 'U';
  return name.trim().charAt(0).toUpperCase() || 'U';
}

/**
 * DataGrid columns for the user list, read from the real `UserMgmtResponseDTO`.
 * CMP parity: Username(avatar+displayName+@uName) · Phone/Email · Type · Location · Earning($) ·
 * Login Type · Status.
 */
export const userColumns: ColumnDef<UserMgmtResponseDTO, unknown>[] = [
  {
    id: 'username',
    header: 'Username',
    cell: ({ row }) => {
      const u = row.original;
      return (
        <div className="flex items-center gap-3">
          <Avatar>
            {u.avatarImage && <AvatarImage src={u.avatarImage} alt={u.displayName ?? ''} />}
            <AvatarFallback>{initials(u.displayName ?? u.uName)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium">{u.displayName ?? '—'}</span>
            {u.uName && <span className="text-xs text-muted-foreground">@{u.uName}</span>}
          </div>
        </div>
      );
    },
  },
  {
    id: 'contact',
    header: 'Phone / Email',
    // Prefer phone, then email; show em dash when both are missing (no Tipmi placeholder).
    cell: ({ row }) => (
      <span>{row.original.phoneNumber || row.original.email || '—'}</span>
    ),
  },
  {
    id: 'type',
    header: 'Type',
    // Backend serialises UserType as its code (N/C/A) — map to Creator/Normal/Admin.
    cell: ({ row }) => <span>{userTypeLabel(row.original.userType)}</span>,
  },
  {
    id: 'role',
    header: 'Role',
    // getUsers SQL already GROUP_CONCATs role names into roleName; surface it.
    cell: ({ row }) => <span>{row.original.roleName || '—'}</span>,
  },
  {
    id: 'location',
    header: 'Location',
    cell: ({ row }) => <span>{row.original.location ?? '—'}</span>,
  },
  {
    id: 'earning',
    header: 'Earning',
    cell: ({ row }) => {
      const earning = row.original.earning;
      return <span>{earning != null ? `$${earning}` : '—'}</span>;
    },
  },
  {
    id: 'loginType',
    header: 'Login Type',
    cell: ({ row }) => <LoginProviderCell loginMethod={row.original.loginMethod ?? null} />,
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const { label, variant } = userStatusDisplay(
        row.original.userStatus,
        row.original.deactivatedAt,
      );
      return <Badge variant={variant}>{label}</Badge>;
    },
  },
];
